import { useEffect } from 'react';
import { useRef } from 'react';
import { getSessionEnvironment } from './session-environment';
import { createSessionValidator } from './session-status';
import { resolveStatusInterval } from './session-status';
import { runSessionStatus } from './session-status';
import { sessionStatusLockName } from './session-status';
import { notifyUnauthenticated } from './use-session-broadcast';
import { broadcastOrgSwitch } from './use-org-sync';
import type { LockRequestFn } from './session-status';
import type { OrganizationSelection } from './types';

type UseSessionStatusOptions = {
  redirectUrl: string;
  resolveRedirectUrl?: () => string;
  onOrgChange?: (selection: OrganizationSelection) => void;
  onOrgDeactivated?: () => void;
  enabled?: boolean;
};

export function useSessionStatus({
  redirectUrl,
  resolveRedirectUrl,
  onOrgChange,
  onOrgDeactivated,
  enabled = true,
}: UseSessionStatusOptions): void {
  const onOrgChangeRef = useRef(onOrgChange);
  useEffect(() => {
    onOrgChangeRef.current = onOrgChange;
  }, [onOrgChange]);

  const onOrgDeactivatedRef = useRef(onOrgDeactivated);
  useEffect(() => {
    onOrgDeactivatedRef.current = onOrgDeactivated;
  }, [onOrgDeactivated]);

  const resolveRedirectUrlRef = useRef(resolveRedirectUrl);
  useEffect(() => {
    resolveRedirectUrlRef.current = resolveRedirectUrl;
  }, [resolveRedirectUrl]);

  useEffect(() => {
    let cleanup: (() => void) | undefined = undefined;
    if (enabled && typeof window !== 'undefined') {
      let lockRequest: LockRequestFn | null = null;
      const lockManager = (navigator as Navigator & { locks?: LockManager }).locks;
      if (typeof navigator !== 'undefined' && lockManager) {
        lockRequest = lockManager.request.bind(lockManager) as LockRequestFn;
      }
      const isProduction = getSessionEnvironment() === 'production';
      cleanup = runSessionStatus({
        intervalMs: resolveStatusInterval(isProduction),
        lockName: sessionStatusLockName,
        lockRequest: lockRequest,
        validate: createSessionValidator(window.fetch.bind(window)),
        onUnauthenticated: () => {
          let target = redirectUrl;
          const resolver = resolveRedirectUrlRef.current;
          if (resolver) {
            target = resolver();
          }
          notifyUnauthenticated(target);
        },
        onOrgDeactivated: () => {
          const handler = onOrgDeactivatedRef.current;
          if (handler) {
            handler();
          }
        },
        onOrgChange: (selection, switched) => {
          const handler = onOrgChangeRef.current;
          if (handler) {
            handler(selection);
          }
          if (switched) {
            broadcastOrgSwitch(selection);
          }
        },
        setInterval: (callback, ms) => {
          return window.setInterval(callback, ms);
        },
        clearInterval: (handle) => {
          window.clearInterval(handle);
        },
      });
    }
    return cleanup;
  }, [redirectUrl, enabled]);
}
